import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import '../styles/manageCategory.css';
import { toast } from 'react-toastify';
import "../styles/dataTable.css";
import "../styles/floatingForm.css";
import searchIcon from '../assets/images/search_icon.svg';
import addIcon from '../assets/images/add-icon.svg';
import arrowIcon from '../assets/images/Arrow.svg';
import upwardArrowIcon from '../assets/images/upward_arrow.svg';
import editIcon from '../assets/images/edit_icon.svg';
import gsap from 'gsap';

const ManageDepartureCity = () => {
  const [cities, setCities] = useState([]);
  const [cityName, setCityName] = useState('');
  const [airportName, setAirportName] = useState('');
  const [cityId, setCityId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const containerRef = useRef(null);

  // Table states
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // Filter & Sort
  const filteredData = cities.filter(item =>
    item.dcityname.toLowerCase().includes(search.toLowerCase())
  );

  if (sortField) {
    filteredData.sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });
  }

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [search, entriesPerPage, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortOrder("asc"); }
  };

  const fetchCities = async () => {
    try {
      const response = await api.get('/departurecities/get-all');
      setCities(response.data.data);
    } catch (error) { console.error(error); }
  };

  useEffect(() => { fetchCities(); }, [showModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (!cityName.trim()) errors.cityName = "City Name cannot be empty";
    if (!airportName.trim()) errors.airportName = "Airport Name cannot be empty";
    if (Object.keys(errors).length > 0) { setErrors(errors); return; }
    setErrors({});

    try {
      if (cityId) {
        const response = await api.post(`/departurecities/update/${cityId}`, { dcityname: cityName, airportname: airportName    });
        response.data.status === "success"
          ? toast.success("City updated successfully!")
          : toast.error("Failed to update city");
      } else {
        const response = await api.post('/departurecities/add', { dcityname: cityName, airportname: airportName   });
        response.data.status === "success"
          ? toast.success("City created successfully!")
          : toast.error("Failed to create city");
      }
      setCityName('');
      setAirportName("");
      setCityId(null);
      setShowModal(false);
      fetchCities();
    } catch (error) { toast.error("Error saving city"); }
  };

  const handleEdit = (city) => {
    setCityId(city.id);
    setCityName(city.dcityname);
    setAirportName(city.airportname);
    setShowModal(true);
  };

  useEffect(() => {
    if (showModal && containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        { autoAlpha: 0, y: 50, scale: 0.95 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "power2.out" }
      );
    }
  }, [showModal]);

  const handleCancel = () => {
    if (containerRef.current) {
      gsap.to(containerRef.current, {
        autoAlpha: 0,
        y: 50,
        scale: 0.95,
        duration: 0.4,
        ease: "power2.in",
        onComplete: () => setShowModal(false),
      });
    } else setShowModal(false);
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Table Section */}
      <div style={{ padding: "20px", opacity: showModal ? 0.5 : 1, pointerEvents: showModal ? "none" : "auto" }}>
        <div className="table-header">
          <h2 style={{ fontWeight: 700 }}>Manage Departure City</h2>
          <div className="search-container">
            <input type="text" placeholder="Search" value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} />
            <img src={searchIcon} alt="search-icon" className="search-icon" />
          </div>
          <div className="entries-container">
            <label>Show: </label>
            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
              <option value={10}>10 Entries per page</option>
              <option value={50}>50 Entries per page</option>
              <option value={100}>100 Entries per page</option>
            </select>
          </div>
          <div className="action-btn">
            <button onClick={() => { setCityId(null); setCityName(''); setErrors({}); setShowModal(true); }}>
              <img src={addIcon} alt="add-icon" /> Add City
            </button>
          </div>
        </div>

        <table width="100%" cellPadding="10" className="table-no-outer-border ">
          <thead>
            <tr>
              <th style={{width:"10%",textAlign:"center"}}>Sr. No.</th>
              <th onClick={() => handleSort("dcityname")} style={{textAlign:"center"}}>
                City Name
                <div className='sort-arrow-container'>
                  <img src={upwardArrowIcon} alt="up" style={{ opacity: sortField === "dcityname" && sortOrder === "asc" ? 1 : 0.5 }} />
                  <img src={upwardArrowIcon} alt="down" style={{ transform: 'rotateX(180deg)', opacity: sortField === "dcityname" && sortOrder === "desc" ? 1 : 0.5 }} />
                </div>
              </th>
              <th style={{textAlign:"center"}}>Airport Name</th>
              <th style={{width:"20%",textAlign:"center"}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? paginatedData.map((city, i) => (
              <tr key={city.id}>
                <td style={{textAlign:"center"}}>{(currentPage - 1) * entriesPerPage + i + 1}</td>
                <td style={{textAlign:"Center"}}>{city.dcityname}</td>
                <td style={{textAlign:"center"}}>{city.airportname}</td>
                <td  className="text-center" style={{ display: "flex", justifyContent: "center" }}>
                  <button className="edit-button" onClick={() => handleEdit(city)}>
                    <img src={editIcon} alt="edit-icon" /> Edit
                  </button>
                </td>
              </tr>
            )) : <tr><td colSpan={3} style={{ textAlign: "center" }}>No data found</td></tr>}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ paddingBottom: '30px', background: '#fff' }}>
          <div style={{ padding: "30px", textAlign: "center", borderTop: '2px solid #F2F2F2', borderBottom: '2px solid #F2F2F2' }}>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{margin: "0px 5px",padding:" 5px 10px",color: "black",border: "none",cursor:" pointer"}}>
              <img src={arrowIcon} alt="prev" style={{ transform: 'rotateY(0deg)' }} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => setCurrentPage(page)}
                style={{ margin: "0 5px", background: currentPage === page ? "#D9D9D9" : "#fff" ,border: "none",}}>{page}</button>
            ))}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}style={{margin: "0px 5px", padding:" 5px 10px",color: "black",border: "none",cursor:" pointer"}}>
              <img src={arrowIcon} alt="next" style={{ transform: 'rotateY(180deg)' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Floating Modal */}
      {showModal && (
        <div ref={containerRef} className="floating-form-container smaller">
          <div className="heading">
            <h2>{cityId ? "Edit City" : "Add City"}</h2>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-container">
              <div className="section-1">
                <div className="input-container-floating-form wid-100">
                  <label>City Name *</label>
                  <input type="text" className={`form-control ${errors.cityName ? 'is-invalid' : ''}`} value={cityName} onChange={e => setCityName(e.target.value)} />
                  {errors.cityName && <span className="text-danger small">{errors.cityName}</span>}
                </div>
                <div className="input-container-floating-form wid-100">
                  <label>Airport name *</label>
                  <input type="text" className={`form-control ${errors.airportName ? 'is-invalid' : ''}`} value={airportName} onChange={e => setAirportName(e.target.value)} />
                  {errors.airportName && <span className="text-danger small">{errors.airportName}</span>}
                </div>
              </div>
            </div>
            <div className="form-btn-container">
              <button type="button" onClick={handleCancel}>Cancel</button>
              <button type="submit">{cityId ? "Update" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManageDepartureCity;
